import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';

// Global "from this day onwards" date filter, shared by every
// time-windowed view so one control in the top bar slices the whole
// dashboard. `from` is a YYYY-MM-DD calendar date interpreted in the report
// timezone (Pacific — see backend REPORT_TZ) or null = no anchor (each view
// uses its own default relative window). Persisted to localStorage so the
// choice survives reloads.

const STORAGE_KEY = 'funnel.dateFrom';
const DATE_RE = /^\d{4}-\d{2}-\d{2}$/;

// Back-compat fallback for the data-trust floor (the rich-events cutover).
// The AUTHORITATIVE floor is served by the backend (lib/time-window.ts
// DATA_START, env-overridable) on /api/auth/me and flows in via the
// DateRangeProvider `dataStart` prop — so there's no drift if ops moves the
// floor. This constant is only used when the server didn't supply one (older
// backend / lockedOut edge). Data before the floor is untrustworthy and
// hard-clamped out server-side; the picker mirrors the floor so the UI can't
// request (or appear to show) pre-floor data.
export const DEFAULT_DATA_START = '2026-05-25';

/** Clamp a YYYY-MM-DD up to the floor (string compare valid for ISO dates). */
export function clampToFloor(date: string, floor: string): string {
  return date >= floor ? date : floor;
}

// Report timezone — mirrors the backend REPORT_TZ (Pacific). "today" resolves
// HERE so plain-language window labels match how the server buckets days.
const REPORT_TZ = 'America/Los_Angeles';

/** Today as YYYY-MM-DD in the report timezone (DST-correct). */
export function todayReportTz(): string {
  return new Intl.DateTimeFormat('en-CA', {
    timeZone: REPORT_TZ,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(new Date());
}

/** `n` days ago as YYYY-MM-DD in the report timezone (Pacific) — pure calendar
 * arithmetic anchored at UTC midnight so it's exactly `n` days earlier.
 * (Moved here from lib/section-window when the period store was unified.) */
export function daysAgoReportTz(n: number): string {
  const anchor = Date.parse(`${todayReportTz()}T00:00:00Z`);
  return new Date(anchor - n * 86_400_000).toISOString().slice(0, 10);
}

/** Render a YYYY-MM-DD as a friendly date, e.g. "May 25, 2026". Built at UTC
 * midnight and rendered in UTC so the calendar day never shifts in a
 * behind-UTC local zone (e.g. Pacific would otherwise show the previous day). */
function friendlyDate(d: string): string {
  const [y, m, day] = d.split('-').map(Number);
  return new Date(Date.UTC(y, (m ?? 1) - 1, day ?? 1)).toLocaleDateString(undefined, {
    timeZone: 'UTC',
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

/**
 * Plain-language description of the active window, which is always [from, today]
 * (the top-bar anchor has no upper bound). Used by metric ⓘ tooltips so the
 * timeframe each number covers reads in plain terms — e.g.
 *   "Since cutover · May 25, 2026 → today"  |  "Today only (Jun 5, 2026, Pacific)"
 *   "Jun 1, 2026 → today"
 */
export function describeWindow(from: string, dataStart: string, to?: string | null): string {
  const today = todayReportTz();
  // Bounded range [from, to] (an explicit end date earlier than today).
  if (to && to < today) {
    const start = from === dataStart ? `since cutover (${friendlyDate(from)})` : friendlyDate(from);
    return `${start} → ${friendlyDate(to)}`;
  }
  if (from >= today) return `Today only (${friendlyDate(today)}, Pacific)`;
  if (from === dataStart) return `Since cutover · ${friendlyDate(from)} → today`;
  return `${friendlyDate(from)} → today`;
}

export interface DateRangeValue {
  // Never null now: the floor is the implicit "earliest" — there is no
  // "all time" below trusted data. Always a YYYY-MM-DD >= the floor.
  from: string;
  /** Optional explicit END date (YYYY-MM-DD). null = open-ended ("→ today").
   *  When set, it's >= from and <= today. */
  to: string | null;
  setFrom: (from: string | null) => void;
  setTo: (to: string | null) => void;
  /** Set both bounds atomically (the range picker) — clamps to[floor,today] and
   *  drops `to` if it would precede `from`. */
  setRange: (from: string | null, to: string | null) => void;
  /** The active data-trust floor (server-provided). Picker min + earliest. */
  dataStart: string;
}

const DateRangeContext = createContext<DateRangeValue | null>(null);

const TO_SUFFIX = '.to';

function readInitial(floor: string, storageKey: string, defaultFrom: string): string {
  try {
    const v = localStorage.getItem(storageKey);
    if (v && DATE_RE.test(v)) return clampToFloor(v, floor);
  } catch {
    /* private mode / quota — fall through to the default */
  }
  // Nothing persisted yet: land on the section's default window (the trust
  // floor unless the caller passes a narrower defaultFrom), so everyone
  // starts on clean data.
  return clampToFloor(defaultFrom, floor);
}

/** Persisted END date for a section, or null. Validated + floor-clamped; an end
 *  before the floor is meaningless so it's dropped. */
function readInitialTo(floor: string, storageKey: string): string | null {
  try {
    const v = localStorage.getItem(storageKey + TO_SUFFIX);
    if (v && DATE_RE.test(v) && v >= floor) return v;
  } catch {
    /* private mode / quota */
  }
  return null;
}

export function DateRangeProvider({
  children,
  dataStart = DEFAULT_DATA_START,
  storageKey = STORAGE_KEY,
  defaultFrom,
}: {
  children: ReactNode;
  /** Server floor from /api/auth/me; falls back to the cutover constant. */
  dataStart?: string;
  /** localStorage key for the persisted window. Override per section so each
   *  page keeps its own window independently; defaults to the legacy global
   *  key. (Per-section migration — there is no global picker anymore.) */
  storageKey?: string;
  /** Initial window when nothing is persisted yet (YYYY-MM-DD); defaults to
   *  the floor ("since cutover"). */
  defaultFrom?: string;
}) {
  const floor = DATE_RE.test(dataStart) ? dataStart : DEFAULT_DATA_START;
  const initialFrom = defaultFrom && DATE_RE.test(defaultFrom) ? defaultFrom : floor;
  const [from, setFromState] = useState<string>(() => readInitial(floor, storageKey, initialFrom));
  const [to, setToState] = useState<string | null>(() => readInitialTo(floor, storageKey));

  // If the server floor moves above the current selection (e.g. ops advanced
  // DATA_START_DATE), pull the selection up so we never query below it.
  useEffect(() => {
    setFromState((cur) => clampToFloor(cur, floor));
    setToState((cur) => (cur && cur < floor ? null : cur));
  }, [floor]);

  const setFrom = useCallback(
    (next: string | null) => {
      // null / invalid means "earliest" → the floor (no all-time below it).
      const f = next && DATE_RE.test(next) ? clampToFloor(next, floor) : floor;
      setFromState(f);
      // Drop a now-invalid end date (one that would precede the new start).
      setToState((cur) => (cur && cur < f ? null : cur));
    },
    [floor],
  );

  const setTo = useCallback(
    (next: string | null) => {
      if (!next || !DATE_RE.test(next)) {
        setToState(null);
        return;
      }
      const today = todayReportTz();
      const capped = next > today ? today : clampToFloor(next, floor);
      // An end before the current start is meaningless → treat as open-ended.
      setToState(capped < from ? null : capped);
    },
    [floor, from],
  );

  const setRange = useCallback(
    (nf: string | null, nt: string | null) => {
      const f = nf && DATE_RE.test(nf) ? clampToFloor(nf, floor) : floor;
      setFromState(f);
      const today = todayReportTz();
      if (!nt || !DATE_RE.test(nt)) {
        setToState(null);
        return;
      }
      const capped = nt > today ? today : clampToFloor(nt, floor);
      setToState(capped < f ? null : capped);
    },
    [floor],
  );

  useEffect(() => {
    try {
      localStorage.setItem(storageKey, from);
      if (to) localStorage.setItem(storageKey + TO_SUFFIX, to);
      else localStorage.removeItem(storageKey + TO_SUFFIX);
    } catch {
      /* private mode / quota — non-fatal, in-memory state still works */
    }
  }, [from, to, storageKey]);

  const value = useMemo(
    () => ({ from, to, setFrom, setTo, setRange, dataStart: floor }),
    [from, to, setFrom, setTo, setRange, floor],
  );
  return <DateRangeContext.Provider value={value}>{children}</DateRangeContext.Provider>;
}

/**
 * Provide an EXTERNALLY-OWNED window as the DateRange context — the bridge that
 * lets the unified period store (lib/period.tsx / SectionDateProvider) back the
 * legacy useDateRange() API without a second state copy. Consumers keep calling
 * useDateRange(); the section's usePeriod hook is the single source of truth.
 */
export function ControlledDateRange({
  value,
  children,
}: {
  value: DateRangeValue;
  children: ReactNode;
}) {
  return <DateRangeContext.Provider value={value}>{children}</DateRangeContext.Provider>;
}

export function useDateRange(): DateRangeValue {
  const ctx = useContext(DateRangeContext);
  if (!ctx) throw new Error('useDateRange must be used within DateRangeProvider');
  return ctx;
}

/**
 * Append the active `from` to an API path as a query param. `from` is always
 * set now (>= DATA_START), so the anchor always rides along; the backend
 * additionally hard-clamps to the floor as defense-in-depth. Use in every
 * time-windowed fetch:
 *   apiFetch(withFrom('/api/cohorts/daily-funnel?days_back=14', from))
 */
export function withFrom(path: string, from: string | null, to?: string | null): string {
  let out = path;
  if (from) {
    out += `${out.includes('?') ? '&' : '?'}from=${encodeURIComponent(from)}`;
  }
  // Optional upper bound — only appended when an explicit end date is set, so
  // callers that pass just `from` keep the open-ended ("→ today") behavior.
  if (to) {
    out += `${out.includes('?') ? '&' : '?'}to=${encodeURIComponent(to)}`;
  }
  return out;
}
